import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { AnimatedLoading } from '@actual-app/components/icons/AnimatedLoading';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';
import type {
  IncomeDistributionAllocation,
  IncomeDistributionRule,
} from '@actual-app/finance-engine';

import { Page } from '#components/Page';
import type {
  FinancialOnboardingState,
  ProductFinanceClient,
  ReadyFinancialDashboard,
} from '#components/product-finance/client';
import { productFinanceClient } from '#components/product-finance/client';
import type {
  FinancialCommandCategory,
  FinancialCommandKind,
  FinancialCommandTarget,
} from '#components/product-finance/FinancialCommandDialog';
import { FinancialCommandDialog } from '#components/product-finance/FinancialCommandDialog';
import { FinancialManagement } from '#components/product-finance/FinancialManagement';
import { FinancialOnboarding } from '#components/product-finance/FinancialOnboarding';
import {
  createFinancialDashboardRange,
  getDeviceLocalDate,
} from '#components/product-finance/money';
import {
  PRODUCT_FINANCE_NAMESPACE,
  ProductFinanceI18nProvider,
} from '#components/product-finance/product-i18n';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { PRODUCT_NAME } from '#product-brand';

import { assertNever, EmptyStateCard } from './dashboard-shared';
import { DashboardPageHeader } from './DashboardPageHeader';
import { FinancialDashboard } from './FinancialDashboard';

export type FinancialDashboardPageProps = Readonly<{
  client?: ProductFinanceClient;
  accounts?: readonly AccountEntity[];
  asOfDate?: string;
}>;

export type DashboardLoadState =
  | Readonly<{ status: 'loading' }>
  | Readonly<{
      status: 'onboarding';
      onboarding: FinancialOnboardingState;
    }>
  | Readonly<{ status: 'error' }>
  | Readonly<{
      status: 'ready';
      dashboard: ReadyFinancialDashboard;
      onboarding: FinancialOnboardingState;
      distributionRules: readonly IncomeDistributionRule[];
    }>;

export type ActiveCommand =
  | Readonly<{
      kind: Exclude<FinancialCommandKind, 'emergency-expense'>;
      target?: FinancialCommandTarget;
    }>
  | Readonly<{
      kind: 'emergency-expense';
      goalId: string;
      availableMinorUnits: number;
    }>;

export function FinancialDashboardPage({
  client = productFinanceClient,
  accounts: accountsOverride,
  asOfDate: asOfDateOverride,
}: FinancialDashboardPageProps) {
  const accountQuery = useAccounts();
  const categoryQuery = useCategories();
  const accounts = accountsOverride ?? accountQuery.data ?? [];
  const activeAccounts = accounts.filter(account => !account.closed);
  const categories = useMemo<readonly FinancialCommandCategory[]>(
    () =>
      (categoryQuery.data?.grouped ?? []).flatMap(group =>
        (group.categories ?? [])
          .filter(category => !category.hidden && !group.hidden)
          .map(category => ({
            id: category.id,
            name: category.name,
            isIncome: Boolean(group.is_income),
          })),
      ),
    [categoryQuery.data?.grouped],
  );
  const asOfDate = asOfDateOverride ?? getDeviceLocalDate();
  const request = useMemo(
    () => createFinancialDashboardRange(asOfDate),
    [asOfDate],
  );
  const [loadState, setLoadState] = useState<DashboardLoadState>({
    status: 'loading',
  });
  const [activeCommand, setActiveCommand] = useState<ActiveCommand | null>(
    null,
  );
  const [lastAccountId, setLastAccountId] = useState(readLastAccountId);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const { t } = useTranslation(PRODUCT_FINANCE_NAMESPACE);

  useEffect(() => {
    document.title = `${t('My money')} | ${PRODUCT_NAME}`;
  }, [t]);

  const reload = useCallback(async () => {
    setLoadState({ status: 'loading' });
    try {
      const onboarding = await client.loadOnboarding();
      if (!onboarding.isComplete) {
        setLoadState({ status: 'onboarding', onboarding });
        return;
      }
      const [result, distributionRules] = await Promise.all([
        client.loadDashboard(request),
        client.loadIncomeDistributionRules?.() ?? Promise.resolve([]),
      ]);
      setLoadState(
        result.status === 'ready'
          ? {
              status: 'ready',
              dashboard: result.dashboard,
              onboarding,
              distributionRules,
            }
          : {
              status: 'onboarding',
              onboarding: {
                ...onboarding,
                step: 'accounts',
                isComplete: false,
              },
            },
      );
    } catch {
      setLoadState({ status: 'error' });
    }
  }, [client, request]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function submitCommand(input: {
    accountId: string;
    amountMinorUnits: number;
    date: string;
    description: string;
    categoryId?: string;
    note?: string;
    distribution?: readonly IncomeDistributionAllocation[];
    commandId: string;
  }) {
    if (activeCommand === null || loadState.status !== 'ready') {
      return;
    }
    const currency = loadState.dashboard.currentCash.currency;
    const target = 'target' in activeCommand ? activeCommand.target : undefined;
    switch (activeCommand.kind) {
      case 'expense':
        await client.recordExpense({ ...input, currency });
        break;
      case 'emergency-expense':
        if (client.useEmergencyFund === undefined) {
          throw new Error('Emergency fund usage is not available.');
        }
        await client.useEmergencyFund({
          ...request,
          ...input,
          currency,
          goalId: activeCommand.goalId,
        });
        break;
      case 'extra-income':
        await client.recordExtraIncome({ ...input, currency });
        break;
      case 'expected-income':
        if (target === undefined) {
          throw new Error('Expected income target is required.');
        }
        await client.confirmExpectedIncome({
          accountId: input.accountId,
          amountMinorUnits: input.amountMinorUnits,
          currency,
          date: input.date,
          occurrenceKey: target.occurrenceKey,
          ...(input.description.length === 0
            ? {}
            : { description: input.description }),
          ...(input.categoryId === undefined
            ? {}
            : { categoryId: input.categoryId }),
          ...(input.note === undefined ? {} : { note: input.note }),
          ...(input.distribution === undefined
            ? {}
            : { distribution: input.distribution }),
          commandId: input.commandId,
        });
        break;
      case 'scheduled-payment':
        if (target === undefined) {
          throw new Error('Scheduled payment target is required.');
        }
        await client.confirmScheduledPayment({
          accountId: input.accountId,
          amountMinorUnits: input.amountMinorUnits,
          currency,
          date: input.date,
          occurrenceKey: target.occurrenceKey,
          ...(input.description.length === 0
            ? {}
            : { description: input.description }),
          ...(input.categoryId === undefined
            ? {}
            : { categoryId: input.categoryId }),
          ...(input.note === undefined ? {} : { note: input.note }),
          commandId: input.commandId,
        });
        break;
      case 'edit-movement':
        if (target?.actualFactId === undefined) {
          throw new Error('Actual financial fact target is required.');
        }
        await client.updateMovement({
          ...input,
          actualFactId: target.actualFactId,
          currency,
        });
        break;
      default:
        assertNever(activeCommand);
    }
    rememberLastAccountId(input.accountId);
    setLastAccountId(input.accountId);
    await reload();
  }

  async function deleteMovement(
    actualFactId: string,
    options?: { removeLinkedContributions?: boolean },
  ) {
    await client.deleteMovement({ actualFactId, ...options });
    await reload();
  }

  async function undoReconciliation(
    occurrenceKey: string,
    options?: { removeLinkedContributions?: boolean },
  ) {
    await client.undoReconciliation({ occurrenceKey, ...options });
    await reload();
  }

  async function setGoalContribution(goalId: string, amountMinorUnits: number) {
    if (loadState.status !== 'ready') {
      return;
    }
    await client.setCycleGoalContribution({
      ...request,
      goalId,
      amountMinorUnits,
      currency: loadState.dashboard.currentCash.currency,
    });
    await reload();
  }

  async function recordGoalProgress(input: {
    goalId: string;
    kind: 'contribution' | 'withdrawal';
    amountMinorUnits: number;
    date: string;
    note?: string;
    commandId?: string;
  }) {
    if (loadState.status !== 'ready') {
      return;
    }
    await client.recordGoalProgress({
      ...input,
      currency: loadState.dashboard.currentCash.currency,
    });
    await reload();
  }

  async function deleteGoalProgressEntry(goalId: string, entryId: string) {
    await client.deleteGoalProgressEntry({ goalId, entryId });
    await reload();
  }

  return (
    <ProductFinanceI18nProvider>
      <Page header={<DashboardPageHeader />} padding={12}>
        <View
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            width: '100%',
            maxWidth: 1180,
            alignSelf: 'center',
            paddingBottom: 100,
          }}
        >
          {loadState.status === 'loading' && <FinancialDashboardLoading />}
          {loadState.status === 'onboarding' && (
            <FinancialOnboarding
              initialState={loadState.onboarding}
              accounts={activeAccounts}
              client={client}
              onComplete={async () => {
                setIsEditingSettings(false);
                await reload();
              }}
            />
          )}
          {loadState.status === 'error' && (
            <FinancialDashboardError onRetry={reload} />
          )}
          {loadState.status === 'ready' && !isEditingSettings && (
            <FinancialDashboard
              dashboard={loadState.dashboard}
              onOpenCommand={setActiveCommand}
              onEditSettings={() => setIsEditingSettings(true)}
              onDeleteMovement={deleteMovement}
              onUndoReconciliation={undoReconciliation}
              onSetGoalContribution={setGoalContribution}
              onRecordGoalProgress={recordGoalProgress}
              onDeleteGoalProgressEntry={deleteGoalProgressEntry}
            />
          )}
          {loadState.status === 'ready' && isEditingSettings && (
            <FinancialManagement
              onboarding={loadState.onboarding}
              accounts={activeAccounts}
              client={client}
              request={request}
              onClose={async () => {
                setIsEditingSettings(false);
                await reload();
              }}
            />
          )}
        </View>

        {activeCommand !== null && loadState.status === 'ready' && (
          <FinancialCommandDialog
            kind={activeCommand.kind}
            accounts={activeAccounts}
            categories={categories}
            defaultDate={asOfDate}
            defaultAccountId={selectDefaultAccountId(
              activeAccounts,
              loadState.dashboard.liquidAccountIds,
              lastAccountId,
            )}
            maximumAmountMinorUnits={
              activeCommand.kind === 'emergency-expense'
                ? activeCommand.availableMinorUnits
                : undefined
            }
            target={
              'target' in activeCommand ? activeCommand.target : undefined
            }
            onClose={shouldReviewOutcome => {
              setActiveCommand(null);
              if (shouldReviewOutcome) void reload();
            }}
            incomeDistribution={{
              rules: loadState.distributionRules,
              goals: loadState.dashboard.financialGoals,
              currency: loadState.dashboard.currentCash.currency,
            }}
            onSubmit={submitCommand}
          />
        )}
      </Page>
    </ProductFinanceI18nProvider>
  );
}

const LAST_ACCOUNT_STORAGE_KEY = 'budget.product-finance.last-account.v1';

function readLastAccountId(): string | undefined {
  try {
    return (
      globalThis.localStorage?.getItem(LAST_ACCOUNT_STORAGE_KEY) ?? undefined
    );
  } catch {
    return undefined;
  }
}

function rememberLastAccountId(accountId: string) {
  try {
    globalThis.localStorage?.setItem(LAST_ACCOUNT_STORAGE_KEY, accountId);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
}

function selectDefaultAccountId(
  accounts: readonly Readonly<{ id: string }>[],
  liquidAccountIds: readonly string[],
  lastAccountId: string | undefined,
): string | undefined {
  const availableIds = new Set(accounts.map(account => account.id));
  if (lastAccountId !== undefined && availableIds.has(lastAccountId)) {
    return lastAccountId;
  }
  return (
    liquidAccountIds.find(accountId => availableIds.has(accountId)) ??
    accounts[0]?.id
  );
}

export function FinancialDashboardLoading() {
  return (
    <output
      style={{
        minHeight: 260,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatedLoading style={{ width: 24, height: 24 }} />
      <span style={{ marginTop: 12 }}>
        <Trans>Loading your financial dashboard…</Trans>
      </span>
    </output>
  );
}

export function FinancialDashboardError({
  onRetry,
}: {
  onRetry: () => Promise<void>;
}) {
  return (
    <EmptyStateCard>
      <h2 style={{ marginTop: 0 }}>
        <Trans>We couldn't load your financial dashboard</Trans>
      </h2>
      <p>
        <Trans>Your budget data was not changed. Try loading it again.</Trans>
      </p>
      <Button variant="primary" onPress={() => void onRetry()}>
        <Trans>Try again</Trans>
      </Button>
    </EmptyStateCard>
  );
}
